import { useEffect, useState } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="https://public.readdy.ai/ai/img_res/06f1ea22-781b-4fca-9260-fa71802cbddc.png" alt="OBNOVA Logo" className="h-10 w-10 object-contain" />
            <span className={`text-lg font-medium transition-colors ${scrolled ? 'text-gray-900' : 'text-white'}`}>OBNOVA d.o.o. Jelah</span>
          </div>
          <div className="flex items-center gap-10">
            <button onClick={() => scrollToSection('home')} className={`text-base font-light transition-colors cursor-pointer ${scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white hover:text-gray-200'}`}>Home</button>
            <button onClick={() => scrollToSection('about')} className={`text-base font-light transition-colors cursor-pointer ${scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white hover:text-gray-200'}`}>About</button>
            <button onClick={() => scrollToSection('products')} className={`text-base font-light transition-colors cursor-pointer ${scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white hover:text-gray-200'}`}>Products</button>
            <button onClick={() => scrollToSection('features')} className={`text-base font-light transition-colors cursor-pointer ${scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white hover:text-gray-200'}`}>Features</button>
            <button onClick={() => scrollToSection('contact')} className={`text-base font-light transition-colors cursor-pointer ${scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white hover:text-gray-200'}`}>Contact</button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#1a2332] via-[#1e2a3d] to-[#2a2540]"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-8 w-full grid grid-cols-2 gap-16 items-center">
          <div>
            <h1 className="text-7xl font-bold text-white leading-tight mb-8">
              Modern iOS Applications<br />
              <span className="text-white/90">for Enhanced Security</span>
            </h1>
            <p className="text-xl text-white/80 font-light max-w-lg leading-relaxed">
              Expert team developing VPN and Cleaner solutions for iOS platform
            </p>
          </div>
          
          <div className="flex justify-center">
            <div className="relative w-80 h-96">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-400/20 to-purple-500/20 rounded-3xl blur-3xl"></div>
              <img 
                src="https://public.readdy.ai/ai/img_res/0be045ef987a8b5ff20ec2178f077c28.jpg" 
                alt="iOS App Interface" 
                className="relative z-10 w-full h-full object-contain drop-shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-8">
          <h2 className="text-6xl font-semibold text-center text-[#1A1A1A] mb-24" style={{ fontFamily: 'Georgia, serif' }}>Our Products</h2>
          
          <div className="grid grid-cols-2 gap-16">
            {/* VPN Card */}
            <div className="relative rounded-[32px] bg-gradient-to-br from-[#1e3a8a] to-[#7c3aed] p-20 h-[480px] overflow-hidden">
              <div className="absolute inset-0 opacity-5">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
              </div>
              
              <div className="relative z-10">
                <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mb-16">
                  <i className="ri-shield-check-line text-4xl text-white"></i>
                </div>
                
                <h3 className="text-5xl font-bold text-white mb-10">VPN Protection</h3>
                <p className="text-lg text-white/90 leading-relaxed max-w-md">
                  Secure your connection and protect your privacy with military-grade encryption technology
                </p>
              </div>
            </div>

            {/* Cleaner Card */}
            <div className="relative rounded-[32px] bg-gradient-to-br from-[#f97316] to-[#ec4899] p-20 h-[480px] overflow-hidden">
              <div className="absolute inset-0 opacity-8">
                <div className="absolute top-1/4 right-1/4 w-80 h-80 bg-white/30 rounded-[40%] blur-2xl"></div>
                <div className="absolute bottom-1/3 left-1/3 w-64 h-64 bg-white/20 rounded-[50%] blur-3xl"></div>
              </div>
              
              <div className="relative z-10">
                <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mb-16">
                  <i className="ri-brush-line text-4xl text-white"></i>
                </div>
                
                <h3 className="text-5xl font-bold text-white mb-10">Device Cleaner</h3>
                <p className="text-lg text-white/90 leading-relaxed max-w-md">
                  Optimize storage space and boost performance by removing unnecessary files and cache data
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid grid-cols-2 gap-20 items-center">
            <div>
              <h2 className="text-5xl font-bold text-[#1A1A1A] mb-16">Expert iOS Development Team</h2>
              
              <p className="text-xl text-[#666666] leading-relaxed max-w-lg">
                We specialize in creating premium iOS applications that prioritize security, performance, and user experience. Our team is dedicated to delivering innovative solutions that meet the highest standards of quality and reliability.
              </p>
            </div>
            
            <div className="relative h-[600px]">
              <div className="absolute top-0 right-0 w-96 h-80 bg-gradient-to-br from-cyan-300/15 to-cyan-500/15 rounded-[40px] blur-2xl"></div>
              <div className="absolute top-1/3 left-1/4 w-72 h-72 bg-gradient-to-br from-pink-300/15 to-pink-500/15 rounded-[60px] blur-2xl"></div>
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-br from-purple-300/15 to-purple-500/15 rounded-full blur-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-32 bg-[#1E1E1E]">
        <div className="max-w-7xl mx-auto px-8">
          <div className="flex items-start justify-between mb-32">
            <div className="inline-block px-4 py-2 border border-white rounded-full">
              <span className="text-white text-sm">Technology</span>
            </div>
            <h2 className="text-5xl font-bold text-white max-w-2xl leading-tight">Built with Modern iOS Technologies</h2>
          </div>
          
          <div className="grid grid-cols-4 gap-10">
            {/* Swift */}
            <div className="bg-[#2A2A2A] rounded-3xl overflow-hidden">
              <div className="w-full h-72 bg-gradient-to-br from-orange-500/20 to-red-500/20 flex items-center justify-center">
                <img 
                  src="https://readdy.ai/api/search-image?query=Swift%20programming%20language%20logo%20symbol%20on%20dark%20minimalist%20background%2C%20orange%20bird%20icon%2C%20modern%20tech%20illustration%2C%20clean%20professional%20design%2C%20subtle%20gradient%20lighting%2C%20abstract%20geometric%20shapes&width=340&height=280&seq=tech-swift-icon-001&orientation=squarish" 
                  alt="Swift" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-medium text-white mb-4">Swift</h3>
                <p className="text-base text-[#AAAAAA]">Powerful and intuitive programming language</p>
              </div>
            </div>

            {/* SwiftUI */}
            <div className="bg-[#2A2A2A] rounded-3xl overflow-hidden">
              <div className="w-full h-72 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 flex items-center justify-center">
                <img 
                  src="https://readdy.ai/api/search-image?query=SwiftUI%20framework%20interface%20design%20elements%20on%20dark%20background%2C%20blue%20gradient%20UI%20components%2C%20modern%20app%20development%20illustration%2C%20clean%20minimalist%20style%2C%20layered%20interface%20mockup%2C%20professional%20tech%20aesthetic&width=340&height=280&seq=tech-swiftui-icon-002&orientation=squarish" 
                  alt="SwiftUI" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-medium text-white mb-4">SwiftUI</h3>
                <p className="text-base text-[#AAAAAA]">Modern declarative UI framework</p>
              </div>
            </div>

            {/* Security */}
            <div className="bg-[#2A2A2A] rounded-3xl overflow-hidden">
              <div className="w-full h-72 bg-gradient-to-br from-green-500/20 to-emerald-500/20 flex items-center justify-center">
                <img 
                  src="https://readdy.ai/api/search-image?query=Security%20framework%20shield%20lock%20encryption%20symbols%20on%20dark%20background%2C%20green%20glowing%20protection%20icons%2C%20cybersecurity%20illustration%2C%20modern%20tech%20design%2C%20abstract%20security%20network%20visualization%2C%20professional%20minimalist%20style&width=340&height=280&seq=tech-security-icon-003&orientation=squarish" 
                  alt="Security" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-medium text-white mb-4">Security Frameworks</h3>
                <p className="text-base text-[#AAAAAA]">Advanced encryption and protection</p>
              </div>
            </div>

            {/* CloudKit */}
            <div className="bg-[#2A2A2A] rounded-3xl overflow-hidden">
              <div className="w-full h-72 bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center">
                <img 
                  src="https://readdy.ai/api/search-image?query=CloudKit%20cloud%20storage%20technology%20on%20dark%20background%2C%20purple%20pink%20gradient%20cloud%20computing%20icons%2C%20modern%20data%20sync%20illustration%2C%20minimalist%20tech%20design%2C%20abstract%20network%20connectivity%20visualization%2C%20professional%20aesthetic&width=340&height=280&seq=tech-cloudkit-icon-004&orientation=squarish" 
                  alt="CloudKit" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-medium text-white mb-4">CloudKit</h3>
                <p className="text-base text-[#AAAAAA]">Seamless cloud integration and sync</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-40 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid grid-cols-5 gap-20 items-center">
            <div className="col-span-2">
              <div className="rounded-[40px] overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.08)] h-[500px] w-full">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20person%20using%20iPhone%20with%20VPN%20security%20app%20visible%20on%20screen%2C%20soft%20blue%20lavender%20gradient%20background%2C%20lifestyle%20tech%20photography%2C%20modern%20clean%20aesthetic%2C%20natural%20lighting%2C%20person%20focused%20on%20mobile%20device%2C%20contemporary%20professional%20setting&width=600&height=500&seq=testimonial-user-photo-001&orientation=landscape" 
                  alt="User testimonial" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            
            <div className="col-span-3">
              <div className="text-base text-[#888888] mb-8">(User Feedback)</div>
              
              <h2 className="text-5xl font-semibold mb-20 leading-tight">
                <span className="text-[#1A1A1A]">What Our Users</span><br />
                <span className="text-[#1A1A1A]">Are </span><span className="text-[#999999]">Saying</span>
              </h2>
              
              <blockquote className="mb-8">
                <p className="text-xl text-[#444444] leading-relaxed max-w-xl">
                  <span className="text-[#AAAAAA]">"</span>The VPN app has completely transformed how I browse on my iPhone. Fast connection speeds and intuitive interface make it my go-to security solution.<span className="text-[#AAAAAA]">"</span>
                </p>
              </blockquote>
              
              <p className="text-xl font-bold text-[#1A1A1A] mb-16">— Sarah Mitchell</p>
              
              <div className="flex gap-5">
                <button className="w-12 h-12 rounded-full border-2 border-gray-300 flex items-center justify-center hover:border-gray-400 transition-colors cursor-pointer">
                  <i className="ri-arrow-left-line text-xl text-gray-600"></i>
                </button>
                <button className="w-12 h-12 rounded-full bg-black flex items-center justify-center hover:bg-gray-800 transition-colors cursor-pointer">
                  <i className="ri-arrow-right-line text-xl text-white"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-40 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://readdy.ai/api/search-image?query=abstract%20flowing%20dynamic%20shapes%20in%20vibrant%20blue%20purple%20pink%20gradient%20creating%20energetic%20movement%2C%20modern%20digital%20art%20background%2C%20smooth%20curves%20and%20waves%2C%20contemporary%20tech%20aesthetic%2C%20high%20contrast%20colorful%20design%2C%20professional%20artistic%20composition&width=1920&height=600&seq=cta-background-gradient-001&orientation=landscape" 
            alt="Background" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-radial from-black/15 via-transparent to-black/15"></div>
          <div className="absolute inset-0 bg-black/20"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-8">
          <div className="flex items-center justify-between">
            <h2 className="text-7xl font-bold text-white max-w-lg leading-tight" style={{ textShadow: '0 2px 20px rgba(0,0,0,0.3)' }}>
              enhance your ios experience
            </h2>
            
            <div className="text-right">
              <p className="text-xl text-white font-light leading-relaxed" style={{ textShadow: '0 2px 20px rgba(0,0,0,0.3)' }}>
                Discover powerful applications designed<br />
                to protect and optimize your device
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer / Contact Section */}
      <footer id="contact" className="relative bg-[#0A2F2F] py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-8">
          <svg className="w-full h-full" viewBox="0 0 1200 600" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 300 Q 300 250 600 300 T 1200 300" stroke="rgba(0, 212, 255, 0.1)" strokeWidth="2" fill="none"/>
            <path d="M0 320 Q 300 270 600 320 T 1200 320" stroke="rgba(0, 212, 255, 0.08)" strokeWidth="2" fill="none"/>
          </svg>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-8">
          <div className="grid grid-cols-10 gap-16 mb-24">
            {/* Brand Column */}
            <div className="col-span-3">
              <h3 className="text-2xl font-medium text-white mb-10" style={{ fontFamily: 'Georgia, serif' }}>OBNOVA d.o.o. Jelah</h3>
              
              <p className="text-3xl text-white leading-snug mb-12" style={{ fontFamily: 'Georgia, serif' }}>
                Modern iOS<br />Solutions
              </p>
            </div>
            
            {/* Products Column */}
            <div className="col-span-2">
              <h4 className="text-lg font-medium text-white mb-6">Products</h4>
              <ul className="space-y-4">
                <li><a href="#products" className="text-base text-[#B8B8B8] hover:text-white transition-colors cursor-pointer">VPN Protection</a></li>
                <li><a href="#products" className="text-base text-[#B8B8B8] hover:text-white transition-colors cursor-pointer">Device Cleaner</a></li>
                <li><a href="#features" className="text-base text-[#B8B8B8] hover:text-white transition-colors cursor-pointer">iOS Security</a></li>
              </ul>
            </div>
            
            {/* Company Column */}
            <div className="col-span-2">
              <h4 className="text-lg font-medium text-white mb-6">Company</h4>
              <ul className="space-y-4">
                <li><a href="#about" className="text-base text-[#B8B8B8] hover:text-white transition-colors cursor-pointer">About Us</a></li>
                <li><a href="#about" className="text-base text-[#B8B8B8] hover:text-white transition-colors cursor-pointer">Our Team</a></li>
                <li><a href="#features" className="text-base text-[#B8B8B8] hover:text-white transition-colors cursor-pointer">Technology</a></li>
              </ul>
            </div>
            
            {/* Support Column */}
            <div className="col-span-3">
              <h4 className="text-lg font-medium text-white mb-6">Contact</h4>
              <div className="space-y-6">
                <div>
                  <div className="text-sm text-[#B8B8B8] mb-2 flex items-center gap-2">
                    <i className="ri-map-pin-line"></i>
                    <span>Address:</span>
                  </div>
                  <p className="text-base text-white">Poslovna zona "CIGLANA" 9</p>
                  <p className="text-base text-white mt-1">Jelah, 74264</p>
                  <p className="text-base text-white mt-1">Bosnia &amp; Herzegovina</p>
                </div>
                
                <div>
                  <div className="text-sm text-[#B8B8B8] mb-2 flex items-center gap-2">
                    <i className="ri-mail-line"></i>
                    <span>Email:</span>
                  </div>
                  <a href="mailto:help@obnovadoojelah.com" className="text-base text-white hover:text-[#00D4FF] transition-colors cursor-pointer">help@obnovadoojelah.com</a>
                </div>
                
                <div>
                  <div className="text-sm text-[#B8B8B8] mb-2 flex items-center gap-2">
                    <i className="ri-phone-line"></i>
                    <span>Phone:</span>
                  </div>
                  <a href="tel:+13367170657" className="text-base text-white hover:text-[#00D4FF] transition-colors cursor-pointer">+1 3367170657</a>
                </div>
              </div>
            </div>
          </div>
          
          {/* Copyright Bar */}
          <div className="border-t border-[#2A4A4A] pt-10">
            <div className="flex items-center justify-between">
              <p className="text-sm text-white">© 2025 OBNOVA d.o.o. Jelah. All rights reserved.</p>
              
              <div className="flex items-center gap-6">
                <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg border border-white flex items-center justify-center hover:bg-white/10 transition-colors cursor-pointer">
                  <i className="ri-linkedin-line text-white"></i>
                </a>
                <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg border border-white flex items-center justify-center hover:bg-white/10 transition-colors cursor-pointer">
                  <i className="ri-twitter-x-line text-white"></i>
                </a>
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg border border-white flex items-center justify-center hover:bg-white/10 transition-colors cursor-pointer">
                  <i className="ri-facebook-line text-white"></i>
                </a>
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg border border-white flex items-center justify-center hover:bg-white/10 transition-colors cursor-pointer">
                  <i className="ri-instagram-line text-white"></i>
                </a>
              </div>
              
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-sm text-white hover:text-[#00D4FF] transition-colors cursor-pointer">Powered by Readdy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
